import { CheckIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Props = {
  isYearly: boolean;
  isCurrent: boolean;
  disabled: boolean;
  title: string;
  monthlyPrice?: number;
  yearlyPrice?: number;
  description: string;
  features: string[];
  actionLabel: string;
  popular?: boolean;
  exclusive?: boolean;
  monthlyProductId: string;
  yearlyProductId: string;
  controlsActive: boolean;
  onSelect: (productId: string) => void;
};

const getPriceTag = (isYearly: boolean, monthlyPrice?: number, yearlyPrice?: number) => {
  if (monthlyPrice === undefined || yearlyPrice === undefined) {
    return 'Custom';
  }

  if (isYearly) {
    return `$${yearlyPrice}`;
  }

  return `$${monthlyPrice}`;
};

export const PricingCard = ({
  isYearly,
  disabled,
  title,
  monthlyPrice,
  yearlyPrice,
  description,
  features,
  actionLabel,
  popular,
  exclusive,
  onSelect,
  monthlyProductId,
  yearlyProductId,
  isCurrent,
  controlsActive,
}: Props) => (
  <Card
    className={cn(
      'flex w-72 rounded-3xl inset-shadow-xs inset-shadow-orange-500/25 hover:inset-shadow-orange-500/60 hover:shadow-2xl hover:shadow-orange-800/20 flex-col justify-between py-1 mx-auto sm:mx-0 transition',
      {
        'border-yellow-500 shadow-2xl shadow-orange-800/25 hover:border-yellow-200 hover:shadow-2xl hover:shadow-orange-800/50':
          popular,
        'animate-background-shine bg-white bg-[length:200%_100%] dark:bg-[linear-gradient(110deg,#000103,25%,#0e1621,75%,#000103)]':
          exclusive,
      },
    )}
  >
    <CardHeader className="pt-4">
      {isYearly && yearlyPrice && monthlyPrice ? (
        <div className="flex justify-between relative">
          <CardTitle className="text-lg text-zinc-700 dark:text-zinc-300">{title}</CardTitle>
          <div
            className={cn(
              'h-fit absolute right-[-8px] rounded-full bg-zinc-200 px-2.5 py-1 text-sm text-black dark:bg-zinc-800 dark:text-white border border-muted-foreground/50',
              {
                'bg-linear-to-r border border-orange-300 from-yellow-300/80 to-orange-400/80 dark:from-yellow-700/10 dark:to-orange-900/10 text dark:text-white':
                  popular,
              },
            )}
          >
            Save ${monthlyPrice * 12 - yearlyPrice}
          </div>
        </div>
      ) : (
        <CardTitle className="text-lg text-zinc-700 dark:text-zinc-300">{title}</CardTitle>
      )}
      <div className="flex gap-0.5">
        <h3 className="text-3xl font-bold">{getPriceTag(isYearly, monthlyPrice, yearlyPrice)}</h3>
        <span className="mb-1 flex flex-col justify-end text-sm">
          {yearlyPrice && isYearly ? ' / year' : monthlyPrice ? ' / month' : null}
        </span>
      </div>
      <CardDescription className="h-12 pt-1.5">{description}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-2">
      {features.map((feature) => (
        <div className="flex gap-2" key={feature}>
          <CheckIcon
            size={18}
            className="my-auto text-sky-400 drop-shadow-[0_0px_2px_rgba(0,188,255,1)]"
          />
          <p className="pt-0.5 text-sm text-zinc-700 dark:text-zinc-300">{feature}</p>
        </div>
      ))}
    </CardContent>
    <CardFooter className="pb-5">
      {isCurrent ? (
        <div className="relative inline-flex w-full items-center justify-center font-bold text-orange-900 dark:text-orange-100">
          Current Plan
        </div>
      ) : controlsActive ? (
        <Button
          disabled={disabled}
          onClick={() => onSelect(isYearly ? yearlyProductId : monthlyProductId)}
          className="relative inline-flex w-full items-center justify-center rounded-md bg-black px-6 font-medium text-white transition-colors focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 focus:outline-hidden dark:bg-white dark:text-black"
        >
          <div className="absolute -inset-0.5 -z-10 rounded-lg bg-linear-to-b from-[#c7d2fe] to-[#8678f9] opacity-75 blur-sm" />
          {actionLabel}
        </Button>
      ) : null}
    </CardFooter>
  </Card>
);
